/**
 * WorkoutStore — local-first writes.
 *
 * saveWorkout writes to IndexedDB immediately with a client-generated UUID
 * and syncStatus='pending', then kicks off a background push to the server.
 * If the push fails (offline, server error) the row stays 'pending' and
 * will be retried on the next sync trigger.
 */
import { atom } from 'jotai'
import { db } from '@/lib/db'
import { pushAllPending } from '@/lib/sync'
import { invalidateLocalData } from '@/lib/invalidate'

export async function saveWorkout(params: {
  exerciseKey: string
  exerciseType: 'aerobic' | 'anaerobic'
  startedAt: Date
  endedAt: Date
  durationSeconds: number
  summary?: Record<string, unknown>
}) {
  const id = crypto.randomUUID()
  const now = new Date().toISOString()

  await db.sessions.add({
    id,
    exerciseKey: params.exerciseKey,
    exerciseType: params.exerciseType,
    startedAt: params.startedAt.toISOString(),
    endedAt: params.endedAt.toISOString(),
    durationSeconds: params.durationSeconds,
    summary: params.summary ?? null,
    createdAt: now,
    syncStatus: 'pending',
  })

  await invalidateLocalData()

  // Fire-and-forget push; errors stay in pending queue for next retry
  void pushAllPending().catch(() => { /* silently retry next time */ })

  return { session: { id } }
}

/** Patch an existing session's timing fields. If the row was already
 * synced upstream, mark pendingEdit so the next push hits /update;
 * a still-pending row just re-uses its existing pending state and the
 * eventual /save will carry the new values. */
export const editAerobicSessionAtom = atom(null, async (
  _get, _set,
  params: { sessionId: string; startedAt: Date; durationSeconds: number },
) => {
  const existing = await db.sessions.get(params.sessionId)
  if (!existing) return
  const endedAt = new Date(params.startedAt.getTime() + params.durationSeconds * 1000)
  const wasSynced = existing.syncStatus === 'synced'
  await db.sessions.update(params.sessionId, {
    startedAt: params.startedAt.toISOString(),
    endedAt: endedAt.toISOString(),
    durationSeconds: params.durationSeconds,
    syncStatus: 'pending',
    pendingEdit: wasSynced ? true : (existing.pendingEdit ?? false),
  })
  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

/** Delete a workout session. If the row is still pending (never reached the
 * server) we drop it locally; otherwise we mark pendingDelete and let the
 * next sync push the deletion. */
export const deleteWorkoutSessionAtom = atom(null, async (_get, _set, sessionId: string) => {
  const existing = await db.sessions.get(sessionId)
  if (!existing) return

  if (existing.syncStatus === 'pending' && !existing.pendingDelete) {
    // Never pushed to server — just delete locally, nothing to clean up upstream.
    await db.sessions.delete(sessionId)
  } else {
    await db.sessions.update(sessionId, {
      syncStatus: 'pending',
      pendingDelete: true,
    })
  }

  await invalidateLocalData()
  void pushAllPending().catch(() => {})
})

export async function getExerciseSettings(exerciseKey: string) {
  const row = await db.exerciseSettings.get(exerciseKey)
  if (!row) return { settings: null }
  return {
    settings: {
      plankDuration: row.plankDuration,
      plankRestDuration: row.plankRestDuration,
      curlRestDuration: row.curlRestDuration,
      curlWeight: row.curlWeight,
      pushUpRestDuration: row.pushUpRestDuration,
      dumbbellPressRestDuration: row.dumbbellPressRestDuration,
      dumbbellPressWeight: row.dumbbellPressWeight,
      gluteBridgeRestDuration: row.gluteBridgeRestDuration,
      squatRestDuration: row.squatRestDuration,
    },
  }
}
